/**
 * PokerVault Me Client — Shopify Theme Integration
 *
 * Injected on every page of the merch store via theme.liquid. Runs on page
 * load to resolve the current PokerVault user and expose the result to
 * downstream theme scripts (Phase 04 account popover, entries badge; Phase
 * 05 orders page).
 *
 * Contract:
 *   - Reads the `pv_link_token` cookie (set by PV on login via
 *     pokervault-link.js spec).
 *   - Fetches GET {API_BASE}/api/v1/store/me with `Authorization: Bearer
 *     <token>` (per 01-12 hardening; the endpoint takes bearer JWT, not
 *     cookie credentials — this is why we use `credentials: 'omit'`).
 *   - Caches the response in sessionStorage (30s TTL + window.focus
 *     revalidation beyond 5s).
 *   - Hydrates `[data-pv-*]` attributes globally (name, email, balance,
 *     tier, avatar, auth-state toggles).
 *   - Fires `pv:me-ready` CustomEvent on `document` in all terminal states
 *     (logged-in OR guest — so downstream scripts always get the signal).
 *   - Exposes `window.PV.me` + `window.PV.reload()` for programmatic use.
 *
 * Cookie / 401 handling:
 *   - Cookie attrs match `_set_shopify_link_cookie` backend helper:
 *     Domain=.pokervault.com, Path=/, Secure, SameSite=Lax.
 *   - On 401 the server has invalidated the token (expired, revoked, user
 *     deactivated). Clear the cookie client-side with matching attrs so
 *     it stops getting sent on subsequent requests. Then fall through to
 *     the guest state so the UI reflects reality immediately.
 *
 * Expected `/store/me` response (see 01-04-SUMMARY):
 *   {
 *     display_name: string,        // never null (falls back to email local-part)
 *     first_name: string,
 *     email: string,
 *     avatar_url: string | null,
 *     tier: { name: string, code: string } | null,
 *     balance: number              // entries balance (int)
 *   }
 *
 * `pv:me-ready` event contract:
 *   Event type: 'pv:me-ready'
 *   Target:     document
 *   detail:     { me: MeResponse | null }
 *   Fires:      exactly once per page load (in logged-in and guest paths);
 *               additionally on every successful background refetch
 *               (window.focus revalidation, programmatic reload()).
 *
 * Data-attribute hydration map:
 *   [data-pv-entries-balance]        textContent = me.balance.toLocaleString()
 *   [data-pv-user-name]              textContent = me.display_name || me.first_name || ''
 *   [data-pv-user-email]             textContent = me.email
 *   [data-pv-user-tier]              textContent = me.tier?.name || ''; hide parent if empty
 *   [data-pv-user-avatar] (<img>)    src = me.avatar_url (only if present)
 *   [data-pv-user-initial]           textContent = first grapheme of first_name / email local-part
 *   [data-pv-auth-state-logged-in]   display: '' when logged-in, 'none' when guest
 *   [data-pv-auth-state-guest]       display: '' when guest, 'none' when logged-in
 *
 * XSS safety: every hydration path uses textContent or setAttribute. No
 * innerHTML anywhere. Avatar URLs from the API are passed to `img.src`
 * which encodes them into a URL context (browsers reject `javascript:`
 * URIs on most image loaders, but this is defense in depth — the API
 * response is already validated server-side).
 *
 * No external dependencies. Online Store 2.0 compatible. Vanilla ES5+.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------

  // Env-suffix the cookie name so the prod cookie on .pokervault.com
  // doesn't bleed into staging/local subdomains with the wrong signing
  // secret. Backend keeps ShopifyConfig.link_token_cookie_name in sync.
  //   merch.pokervault.com         → pv_link_token
  //   merch.staging.pokervault.com → pv_link_token_stg
  //   merch.local.pokervault.com   → pv_link_token_local
  function _pvCookieName() {
    var parts = window.location.hostname.split('.');
    if (parts.length >= 4) {
      var env = parts[1];
      if (env === 'staging') return 'pv_link_token_stg';
      if (env === 'local') return 'pv_link_token_local';
    }
    return 'pv_link_token';
  }
  var COOKIE_NAME = _pvCookieName();
  var CACHE_KEY = 'pv_me';
  var CACHE_TTL_MS = 30 * 1000; // 30s — matches backend Redis TTL on `me:<user_id>`
  var FOCUS_STALE_MS = 5 * 1000; // revalidate on focus if cache older than 5s
  var FETCH_TIMEOUT_MS = 3000; // 3s — matches MASTER-PLAN §2.3 / pokervault-copy.js
  var RETRY_DELAY_MS = 2000; // 2s backoff between the two attempts on network/5xx

  // Derive the PokerVault API URL from the current hostname. Same algorithm
  // as pokervault-link.js:41-43 / pokervault-copy.js:81-83 — swap the first
  // subdomain label for "api".
  //   merch.pokervault.com         -> api.pokervault.com
  //   merch.staging.pokervault.com -> api.staging.pokervault.com
  var _parts = window.location.hostname.split('.');
  _parts[0] = 'api';
  var API_BASE = 'https://' + _parts.join('.');
  var ME_ENDPOINT = API_BASE + '/api/v1/store/me';

  // Cookie attrs used when clearing an invalid token on 401. Must match the
  // attrs used by the backend's `_set_shopify_link_cookie` (which uses
  // `Domain=.{app_config.domain}`) so the browser identifies them as the
  // same cookie. Per RFC 6265 §5.3, an attribute mismatch turns the clear
  // into a no-op targeting a different cookie — the real one stays alive.
  //
  // Derive the domain by stripping the leftmost hostname label, matching
  // the backend's convention across envs:
  //   merch.pokervault.com         -> .pokervault.com
  //   merch.staging.pokervault.com -> .staging.pokervault.com
  //   merch.local.pokervault.com   -> .local.pokervault.com
  var COOKIE_DOMAIN =
    '.' + window.location.hostname.split('.').slice(1).join('.');
  var COOKIE_CLEAR_ATTRS =
    'Max-Age=0; Path=/; Domain=' + COOKIE_DOMAIN + '; Secure; SameSite=Lax';

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  /** @type {object | null} The resolved user, or null for guest. */
  var currentMe = null;

  /** @type {boolean} True once we've fired the initial `pv:me-ready`. */
  var initialReadyFired = false;

  /** @type {Promise<object | null> | null} De-dupe concurrent fetches. */
  var inflightFetch = null;

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Read a cookie value by name. Tight regex anchors — refuses to match a
   * cookie whose name is a suffix of another (e.g., `foo_pv_link_token`
   * wouldn't resolve when reading `pv_link_token`).
   *
   * Returns null if not found.
   */
  function getCookie(name) {
    // Escape regex metachars in the cookie name, just in case.
    var safeName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var match = document.cookie.match(
      new RegExp('(?:^|;\\s*)' + safeName + '=([^;]*)')
    );
    return match ? decodeURIComponent(match[1]) : null;
  }

  /**
   * Clear the link-token cookie client-side using attrs that match the
   * server's `Set-Cookie` header (Domain + Path). Without matching attrs
   * the browser treats the `Max-Age=0` header as a different cookie and
   * the original stays alive until its real expiry.
   */
  function clearLinkCookie() {
    document.cookie = COOKIE_NAME + '=; ' + COOKIE_CLEAR_ATTRS;
  }

  /**
   * fetch() with a timeout via AbortController. Resolves with the Response
   * (caller must inspect .ok / .status) or rejects on network error or
   * abort. Does NOT parse the body.
   *
   * `credentials: 'omit'` is intentional — 01-09 split the CORS policy so
   * `api.pokervault.com` rejects credentialed requests from store origins.
   * We authenticate via bearer token, not cookies.
   */
  function fetchWithTimeout(url, init, timeoutMs) {
    if (typeof AbortController === 'undefined') {
      // Extremely old browsers — fall back to plain fetch without timeout.
      return fetch(url, init);
    }
    var ctrl = new AbortController();
    var timer = setTimeout(function () {
      ctrl.abort();
    }, timeoutMs);
    var merged = {};
    for (var k in init) {
      if (Object.prototype.hasOwnProperty.call(init, k)) {
        merged[k] = init[k];
      }
    }
    merged.signal = ctrl.signal;
    merged.credentials = 'omit';
    return fetch(url, merged).then(
      function (res) {
        clearTimeout(timer);
        return res;
      },
      function (err) {
        clearTimeout(timer);
        throw err;
      }
    );
  }

  /**
   * sleep N ms via a Promise.
   */
  function sleep(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  /**
   * Read the cached me response. Returns null if missing, malformed, or
   * expired. Never throws.
   */
  function readCache() {
    try {
      var raw = window.sessionStorage.getItem(CACHE_KEY);
      if (!raw) {
        return null;
      }
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || !parsed.me || !parsed.ts) {
        return null;
      }
      if (Date.now() - parsed.ts > CACHE_TTL_MS) {
        return null;
      }
      return parsed;
    } catch (_err) {
      return null;
    }
  }

  /**
   * Persist the resolved me response to sessionStorage with a timestamp.
   * Never throws — quota-exceeded / disabled-storage errors are swallowed.
   */
  function writeCache(me) {
    try {
      window.sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ me: me, ts: Date.now() })
      );
    } catch (_err) {
      // Quota exceeded or disabled — continue; in-memory state still works.
    }
  }

  /**
   * Clear the me cache. Called on logout / 401 / cookie-absent transitions.
   */
  function clearCache() {
    try {
      window.sessionStorage.removeItem(CACHE_KEY);
    } catch (_err) {
      // ignore
    }
  }

  /**
   * Return a human-safe toLocaleString for a number. Falls back to plain
   * toString for non-numeric inputs so a malformed API payload doesn't
   * crash hydration. Guards against Intl/locale API failures.
   */
  function formatNumber(n) {
    if (typeof n !== 'number' || !isFinite(n)) {
      return n == null ? '' : String(n);
    }
    try {
      return n.toLocaleString();
    } catch (_err) {
      return String(n);
    }
  }

  /**
   * Extract the first grapheme from a string (correctly handles unicode —
   * surrogate pairs, emoji). Returns '' on empty/invalid input.
   *
   * Array.from() iterates by code point (not code unit) so a 2-char emoji
   * like "👤" returns "👤" rather than a broken lead surrogate.
   */
  function firstGrapheme(str) {
    if (typeof str !== 'string' || !str) {
      return '';
    }
    var chars = Array.from(str.trim());
    return chars.length ? chars[0] : '';
  }

  /**
   * Compute the user's initial letter from available identity fields.
   * Priority: first_name > display_name > email local-part.
   */
  function resolveInitial(me) {
    if (!me) {
      return '';
    }
    var candidate = me.first_name || me.display_name || '';
    if (!candidate && typeof me.email === 'string') {
      var at = me.email.indexOf('@');
      candidate = at > 0 ? me.email.slice(0, at) : me.email;
    }
    return firstGrapheme(candidate).toUpperCase();
  }

  // ---------------------------------------------------------------------------
  // Network
  // ---------------------------------------------------------------------------

  /**
   * Perform a single GET /store/me request with bearer auth.
   * Resolves with:
   *   { ok: true, me }         — 200 + valid JSON
   *   { ok: false, status: 401 } — token rejected (caller clears cookie)
   *   { ok: false, retryable: true }  — network error or 5xx (caller retries once)
   *   { ok: false }            — 4xx other than 401 (caller treats as guest)
   */
  function fetchMeOnce(token) {
    return fetchWithTimeout(
      ME_ENDPOINT,
      {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + token,
          Accept: 'application/json'
        }
      },
      FETCH_TIMEOUT_MS
    ).then(
      function (res) {
        if (res.status === 401) {
          return { ok: false, status: 401 };
        }
        if (res.status >= 500) {
          return { ok: false, retryable: true };
        }
        if (!res.ok) {
          return { ok: false, status: res.status };
        }
        return res.json().then(
          function (me) {
            if (!me || typeof me !== 'object') {
              return { ok: false };
            }
            return { ok: true, me: me };
          },
          function () {
            return { ok: false };
          }
        );
      },
      function () {
        // Network error or abort (timeout).
        return { ok: false, retryable: true };
      }
    );
  }

  /**
   * Fetch the user with one retry on transient failure.
   * Resolves to the me object, or null on any terminal failure.
   *
   * On 401 — clears the cookie so subsequent reads see guest state.
   */
  function fetchMeWithRetry(token) {
    return fetchMeOnce(token).then(function (result) {
      if (result.ok) {
        return result.me;
      }
      if (result.status === 401) {
        clearLinkCookie();
        clearCache();
        return null;
      }
      if (!result.retryable) {
        return null;
      }
      // Transient failure — one retry after backoff.
      return sleep(RETRY_DELAY_MS).then(function () {
        return fetchMeOnce(token).then(function (second) {
          if (second.ok) {
            return second.me;
          }
          if (second.status === 401) {
            clearLinkCookie();
            clearCache();
            return null;
          }
          // Still failing — log and treat as guest. Don't clear the cookie:
          // network blip shouldn't force re-auth on next page load.
          if (typeof console !== 'undefined' && console.warn) {
            console.warn('[pokervault-me] /store/me unreachable; treating as guest');
          }
          return null;
        });
      });
    });
  }

  /**
   * Resolve the current user: cache-first, else network.
   * De-dupes concurrent callers by returning an in-flight promise if one
   * exists (prevents two near-simultaneous fetches when focus fires right
   * as the page loads).
   */
  function resolveMe(opts) {
    var forceRefresh = opts && opts.force;

    if (inflightFetch) {
      return inflightFetch;
    }

    var token = getCookie(COOKIE_NAME);
    if (!token) {
      clearCache();
      return Promise.resolve(null);
    }

    if (!forceRefresh) {
      var cached = readCache();
      if (cached && cached.me) {
        return Promise.resolve(cached.me);
      }
    }

    inflightFetch = fetchMeWithRetry(token)
      .then(function (me) {
        if (me) {
          writeCache(me);
        } else {
          clearCache();
        }
        return me;
      })
      .catch(function () {
        return null;
      })
      .then(function (me) {
        inflightFetch = null;
        return me;
      });

    return inflightFetch;
  }

  // ---------------------------------------------------------------------------
  // DOM hydration
  // ---------------------------------------------------------------------------

  /**
   * Set textContent on every element matching `selector`, using `value`.
   * Empty or null values clear the field (safe default).
   */
  function setText(selector, value) {
    var nodes = document.querySelectorAll(selector);
    var text = value == null ? '' : String(value);
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = text;
    }
  }

  /**
   * Apply auth-state visibility classes.
   *   - `[data-pv-auth-state-logged-in]` → hidden when guest, visible when logged-in
   *   - `[data-pv-auth-state-guest]`     → visible when guest, hidden when logged-in
   *
   * Uses the HTML5 `hidden` attribute (accessible; matches `display: none`
   * semantics in assistive tech, auto-handled by Shopify's render pipeline).
   */
  function applyAuthState(isLoggedIn) {
    var loggedInNodes = document.querySelectorAll('[data-pv-auth-state-logged-in]');
    for (var i = 0; i < loggedInNodes.length; i++) {
      if (isLoggedIn) {
        loggedInNodes[i].removeAttribute('hidden');
      } else {
        loggedInNodes[i].setAttribute('hidden', '');
      }
    }
    var guestNodes = document.querySelectorAll('[data-pv-auth-state-guest]');
    for (var j = 0; j < guestNodes.length; j++) {
      if (isLoggedIn) {
        guestNodes[j].setAttribute('hidden', '');
      } else {
        guestNodes[j].removeAttribute('hidden');
      }
    }
  }

  /**
   * Hydrate every `[data-pv-*]` attribute with the given `me` object.
   * Safe to call repeatedly — idempotent. Called in both initial + refresh
   * paths.
   *
   * When `me` is null (guest), the logged-in fields are cleared so stale
   * text from a previous (now-expired) session doesn't flash.
   */
  function hydrate(me) {
    if (me) {
      // Balance — toLocaleString for readable grouping (e.g., "1,234").
      setText('[data-pv-entries-balance]', formatNumber(me.balance));

      // Name — display_name is always populated server-side (falls back
      // to email local-part per 01-04 shell-account handling), but be
      // defensive in case the contract ever changes.
      var name = me.display_name || me.first_name || '';
      setText('[data-pv-user-name]', name);

      // Email — always a string.
      setText('[data-pv-user-email]', me.email);

      // Tier — optional. When null, hide the parent so we don't render an
      // empty badge / pill. Parent is hidden via the `hidden` attribute
      // (same mechanism as auth-state toggles; CSS can override if needed).
      var tierNodes = document.querySelectorAll('[data-pv-user-tier]');
      var tierName = me.tier && me.tier.name ? me.tier.name : '';
      for (var t = 0; t < tierNodes.length; t++) {
        tierNodes[t].textContent = tierName;
        var parent = tierNodes[t].parentElement;
        if (parent) {
          if (tierName) {
            parent.removeAttribute('hidden');
          } else {
            parent.setAttribute('hidden', '');
          }
        }
      }

      // Avatar — only set src if the API returned a URL. Otherwise leave
      // the element untouched (markup can ship a fallback asset in the
      // default src).
      var avatarNodes = document.querySelectorAll('[data-pv-user-avatar]');
      for (var a = 0; a < avatarNodes.length; a++) {
        var avatarEl = avatarNodes[a];
        if (avatarEl.tagName === 'IMG' && me.avatar_url) {
          // setAttribute escapes the URL into the src attribute context;
          // browsers then URL-parse it. Not a vector for script injection.
          avatarEl.setAttribute('src', me.avatar_url);
        }
      }

      // Initial letter fallback (used beside or in place of avatar when
      // avatar_url is absent / still loading).
      setText('[data-pv-user-initial]', resolveInitial(me));

      applyAuthState(true);
    } else {
      // Guest state — clear logged-in fields, show guest surfaces.
      setText('[data-pv-entries-balance]', '');
      setText('[data-pv-user-name]', '');
      setText('[data-pv-user-email]', '');
      setText('[data-pv-user-tier]', '');
      setText('[data-pv-user-initial]', '');
      applyAuthState(false);
    }
  }

  /**
   * Fire the `pv:me-ready` CustomEvent on `document`. Downstream scripts
   * (Phase 04 popover, entries badge, Phase 05 orders page) listen for
   * this to know when `window.PV.me` is safe to read.
   */
  function fireReady(me) {
    var evt;
    try {
      evt = new CustomEvent('pv:me-ready', { detail: { me: me } });
    } catch (_err) {
      // IE11 fallback — shouldn't fire since OS 2.0 requires evergreen
      // browsers, but defensive.
      evt = document.createEvent('CustomEvent');
      evt.initCustomEvent('pv:me-ready', false, false, { me: me });
    }
    document.dispatchEvent(evt);
  }

  // ---------------------------------------------------------------------------
  // Orchestration
  // ---------------------------------------------------------------------------

  /**
   * Run one resolve → hydrate → event cycle. Used for initial load, focus
   * refresh, and programmatic `window.PV.reload()`.
   *
   * `opts.force` bypasses the sessionStorage cache and forces a network
   * fetch. Useful when the caller knows the cache is stale (e.g., after
   * purchasing entries, a successful checkout, profile update).
   */
  function runCycle(opts) {
    return resolveMe(opts).then(function (me) {
      currentMe = me || null;
      window.PV = window.PV || {};
      window.PV.me = currentMe;
      hydrate(currentMe);
      fireReady(currentMe);
      initialReadyFired = true;
      return currentMe;
    });
  }

  /**
   * On window.focus: if cache is older than FOCUS_STALE_MS, kick off a
   * background refetch. Doesn't block. Keeps the header / badge fresh
   * when the user switches away and comes back.
   */
  function setupFocusRefresh() {
    window.addEventListener('focus', function () {
      var cached = readCache();
      if (!cached || Date.now() - cached.ts > FOCUS_STALE_MS) {
        runCycle({ force: true });
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  window.PV = window.PV || {};
  // Initialize `me` to null so early readers (inline scripts that run
  // before our network fetch completes) get a defined value.
  if (typeof window.PV.me === 'undefined') {
    window.PV.me = null;
  }
  /**
   * Programmatic refresh. Returns a Promise that resolves to the fresh
   * me object (or null). Safe to call repeatedly — in-flight requests
   * are de-duped.
   */
  window.PV.reload = function () {
    return runCycle({ force: true });
  };

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  // Prime from cache synchronously so that inline consumers see a sensible
  // `window.PV.me` value immediately. The network cycle follows for fresh data.
  var primed = readCache();
  if (primed && primed.me) {
    currentMe = primed.me;
    window.PV.me = currentMe;
  }

  // Defer DOM mutation to DOMContentLoaded so we don't race the parser.
  // Script ships with `defer` so the parser has already started / finished
  // most of the body — but being explicit is cheap and correct.
  function startup() {
    runCycle();
    setupFocusRefresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startup);
  } else {
    startup();
  }

  // Defensive: if something prevented `runCycle` from firing (exception in
  // hydrate, for instance), still emit a ready event so downstream scripts
  // don't hang forever. Runs at end of microtask queue.
  Promise.resolve().then(function () {
    if (!initialReadyFired) {
      // Let the real cycle win if it's still in flight.
      setTimeout(function () {
        if (!initialReadyFired) {
          fireReady(currentMe);
        }
      }, FETCH_TIMEOUT_MS + RETRY_DELAY_MS + 500);
    }
  });
})();
